/**
 * i18n — língua fixada em BUILD TIME (padrão da frota APAV).
 *
 * A língua vem de `import.meta.env.VITE_LOCALE`. Não há deteção em runtime nem
 * troca dinâmica: cada build serve exatamente uma língua e a troca faz-se por
 * navegação entre caminhos estáticos (/, /en/, /uk/, …). Nenhum dado do
 * visitante sai da Azure — era esse o problema do widget do GTranslate.
 *
 * Os termos protegidos (`{{apav}}`, `{{number}}`, …) entram por
 * `interpolation.defaultVariables`: assim resolvem em TODAS as chamadas `t()`
 * sem ser preciso passar NO_TRANSLATE em cada uma — e uma chave nova que use
 * um termo protegido nunca aparece com o placeholder cru no ecrã.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { NO_TRANSLATE } from './i18nTerms'

export const SUPPORTED_LOCALES = [
  'pt', 'en', 'uk', 'fr', 'ro', 'ar', 'de', 'es',
  'it', 'nl', 'ru', 'zh', 'bn', 'hi', 'ur',
]
export const RTL_LOCALES = ['ar', 'ur']

const raw = import.meta.env.VITE_LOCALE || 'pt'
export const LOCALE = SUPPORTED_LOCALES.includes(raw) ? raw : 'pt'

// import.meta.glob em vez de imports estáticos: uma língua sem catálogo ainda
// não criado não parte o build, cai para pt. Sem `eager`, cada catálogo fica
// num chunk próprio e só o da língua desta build é pedido pelo browser.
const CATALOGS = import.meta.glob('./locales/*.json')

async function loadCatalog(locale) {
  const carregar = CATALOGS[`./locales/${locale}.json`] ?? CATALOGS['./locales/pt.json']
  const mod = await carregar()
  return mod.default ?? mod
}

const translation = await loadCatalog(LOCALE)

i18n.use(initReactI18next).init({
  lng: LOCALE,
  fallbackLng: false,
  resources: { [LOCALE]: { translation } },
  interpolation: {
    // React já escapa; deixar o i18next escapar de novo estragaria «—», «á», etc.
    escapeValue: false,
    defaultVariables: NO_TRANSLATE,
  },
  returnEmptyString: false,
})

// Em produção o scripts/build-locales.mjs já grava lang/dir no HTML;
// isto garante o mesmo comportamento em `npm run dev`.
if (typeof document !== 'undefined') {
  document.documentElement.lang = LOCALE
  document.documentElement.dir = RTL_LOCALES.includes(LOCALE) ? 'rtl' : 'ltr'
}

export default i18n
